import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { CLAVE_SESION } from '../../lib/sesion.ts';
import { Aviso, Boton, Campo, Cargando, EncabezadoPagina, Insignia, Selector, Tarjeta } from '../../componentes/ui.tsx';
import { impersonarEmpresa } from './Superadmin.tsx';

interface Detalle {
  id: string;
  slug: string;
  codigoExterno: string;
  nombre: string;
  ruc: string | null;
  pais: string;
  estado: 'ACTIVO' | 'INACTIVO';
  fechaExpiracion: string | null;
  vencida: boolean;
  createdAt: string;
  clientes: number;
  productos: number;
  plantillas: number;
  postsUltimos30: number;
}

interface UsuarioEmpresa {
  id: string;
  username: string;
  email: string | null;
  rol: string;
  estado: string;
}

/** Pantalla de edición de una empresa: todos los datos + usuarios (claves incluidas). */
export function PaginaSuperEmpresa() {
  const { empresaId } = useParams({ strict: false }) as { empresaId: string };
  const navegar = useNavigate();
  const qc = useQueryClient();
  const [aviso, setAviso] = useState<string | null>(null);

  const detalle = useQuery({
    queryKey: ['super-empresa', empresaId],
    queryFn: () => api<Detalle>(`/super/empresas/${empresaId}`),
  });
  const usuarios = useQuery({
    queryKey: ['super-empresa-usuarios', empresaId],
    queryFn: () => api<{ items: UsuarioEmpresa[] }>(`/super/empresas/${empresaId}/usuarios`),
  });

  // Formulario controlado, cargado desde el detalle.
  const [nombre, setNombre] = useState('');
  const [slug, setSlug] = useState('');
  const [ruc, setRuc] = useState('');
  const [pais, setPais] = useState('');
  const [codigoExterno, setCodigoExterno] = useState('');
  const [sinExpiracion, setSinExpiracion] = useState(true);
  const [fecha, setFecha] = useState('');
  const [cargado, setCargado] = useState(false);

  useEffect(() => {
    const d = detalle.data;
    if (!d || cargado) return;
    setNombre(d.nombre);
    setSlug(d.slug);
    setRuc(d.ruc ?? '');
    setPais(d.pais);
    setCodigoExterno(d.codigoExterno);
    setSinExpiracion(d.fechaExpiracion === null);
    setFecha(d.fechaExpiracion ? d.fechaExpiracion.slice(0, 10) : '');
    setCargado(true);
  }, [detalle.data, cargado]);

  const guardar = useMutation({
    mutationFn: () =>
      api(`/super/empresas/${empresaId}`, {
        metodo: 'PATCH',
        cuerpo: {
          nombre: nombre.trim(),
          slug: slug.trim(),
          ruc: ruc.trim() || null,
          pais,
          codigoExterno: codigoExterno.trim(),
          // "Sin expiración" manda null explícito (cuenta indefinida).
          fechaExpiracion: sinExpiracion || !fecha ? null : new Date(`${fecha}T23:59:59`).toISOString(),
        },
      }),
    onSuccess: async () => {
      setAviso('Guardado.');
      await qc.invalidateQueries({ queryKey: ['super-empresa', empresaId] });
      await qc.invalidateQueries({ queryKey: ['super-empresas'] });
    },
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  const alternarEstado = useMutation({
    mutationFn: (estado: 'ACTIVO' | 'INACTIVO') =>
      api(`/super/empresas/${empresaId}`, { metodo: 'PATCH', cuerpo: { estado } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-empresa', empresaId] }),
  });

  // ── Usuarios ──
  const [nuevoUsername, setNuevoUsername] = useState('');
  const [nuevoRol, setNuevoRol] = useState('ADMIN_EMPRESA');

  const crearUsuario = useMutation({
    mutationFn: () =>
      api<{ username: string; password: string }>(`/super/empresas/${empresaId}/usuarios`, {
        metodo: 'POST',
        cuerpo: { username: nuevoUsername.trim(), rol: nuevoRol },
      }),
    onSuccess: async (r) => {
      setAviso(`Usuario ${r.username} creado · clave ${r.password} — anótela ahora.`);
      setNuevoUsername('');
      await qc.invalidateQueries({ queryKey: ['super-empresa-usuarios', empresaId] });
    },
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  // Cambiar clave: escribir una propia o dejar vacío para generar una.
  const [claveDe, setClaveDe] = useState<string | null>(null);
  const [claveNueva, setClaveNueva] = useState('');

  const resetearClave = useMutation({
    mutationFn: ({ usuarioId, password }: { usuarioId: string; password?: string }) =>
      api<{ username: string; password: string }>(`/super/usuarios/${usuarioId}/reset-password`, {
        metodo: 'POST',
        cuerpo: password ? { password } : {},
      }),
    onSuccess: (r, variables) => {
      setAviso(
        variables.password
          ? `Clave de ${r.username} cambiada.`
          : `Clave nueva de ${r.username}: ${r.password} — anótela ahora, obliga a cambiarla al entrar.`,
      );
      setClaveDe(null);
      setClaveNueva('');
    },
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  const alternarUsuario = useMutation({
    mutationFn: (u: UsuarioEmpresa) =>
      api(`/super/usuarios/${u.id}`, {
        metodo: 'PATCH',
        cuerpo: { estado: u.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO' },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-empresa-usuarios', empresaId] }),
  });

  if (detalle.isPending) return <Cargando texto="Cargando…" />;
  const d = detalle.data;
  if (!d) return <main className="p-8 text-sm text-suave">Empresa no encontrada.</main>;

  const operativa = d.estado === 'ACTIVO' && !d.vencida;

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina
        atras={() => void navegar({ to: '/super' })}
        titulo={
          <>
            {d.nombre}{' '}
            <span className="align-middle"><Insignia tono={operativa ? 'ok' : 'alerta'}>
              {d.estado === 'INACTIVO' ? 'DESACTIVADA' : d.vencida ? 'VENCIDA' : 'ACTIVA'}
            </Insignia></span>
          </>
        }
        subtitulo={`${d.clientes} clientes · ${d.productos} productos · ${d.plantillas} plantillas · ${d.postsUltimos30} posts/30d · desde ${d.createdAt.slice(0, 10)}`}
        acciones={
          <>
          <Boton
            variante="contorno"
            onClick={() =>
              void impersonarEmpresa(d.id, () => qc.invalidateQueries({ queryKey: CLAVE_SESION })).then(
                (err) => err && setAviso(`Error: ${err}`),
              )
            }
          >
            Entrar en la cuenta
          </Boton>
          <Boton
            variante={d.estado === 'ACTIVO' ? 'secundario' : 'primario'}
            onClick={() => alternarEstado.mutate(d.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO')}
          >
            {d.estado === 'ACTIVO' ? 'Desactivar' : 'Activar'}
          </Boton>
          </>
        }
      />

      {aviso ? <Aviso tono={aviso.startsWith('Error') ? 'error' : 'ok'}>{aviso}</Aviso> : null}

      {/* Datos de la empresa */}
      <form
        className="tarjeta grid gap-3 p-5 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate();
        }}
      >
        <Campo etiqueta="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        <Campo etiqueta="RUC / NIT" value={ruc} onChange={(e) => setRuc(e.target.value)} />
        <Campo etiqueta="Slug (identificador interno)" value={slug} onChange={(e) => setSlug(e.target.value)} required />
        <Campo etiqueta="Código externo" value={codigoExterno} onChange={(e) => setCodigoExterno(e.target.value)} required />
        <Campo etiqueta="País (ISO-2)" value={pais} onChange={(e) => setPais(e.target.value)} required maxLength={2} />

        <fieldset className="sm:col-span-2">
          <legend className="mb-1.5 text-[13px] text-suave">Expiración de la cuenta</legend>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="chip"
              data-activo={sinExpiracion}
              onClick={() => setSinExpiracion(true)}
            >
              Sin expiración (indefinida)
            </button>
            <button
              type="button"
              className="chip"
              data-activo={!sinExpiracion}
              onClick={() => setSinExpiracion(false)}
            >
              Expira el
            </button>
            <input
              type="date"
              value={fecha}
              onChange={(e) => {
                setFecha(e.target.value);
                setSinExpiracion(false);
              }}
              disabled={sinExpiracion}
              className="campo w-48 disabled:opacity-40"
            />
          </div>
          <p className="mt-1 text-xs text-suave">
            Al vencer, la cuenta se desactiva sola y sus usuarios no pueden entrar. Ningún dato se borra.
          </p>
        </fieldset>

        <div className="sm:col-span-2">
          <Boton type="submit" disabled={guardar.isPending}>Guardar cambios</Boton>
        </div>
      </form>

      {/* Usuarios de la empresa */}
      <Tarjeta titulo={`Usuarios (${usuarios.data?.items.length ?? 0})`}>

        <form
          className="mb-3 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            crearUsuario.mutate();
          }}
        >
          <div className="flex-1">
            <Campo
              etiqueta="Nuevo usuario de staff (email)"
              value={nuevoUsername}
              onChange={(e) => setNuevoUsername(e.target.value)}
              required
            />
          </div>
          <Selector etiqueta="Rol" value={nuevoRol} onChange={(e) => setNuevoRol(e.target.value)}>
            <option value="ADMIN_EMPRESA">Admin</option>
            <option value="OPERADOR">Operador</option>
          </Selector>
          <Boton type="submit" disabled={crearUsuario.isPending}>Crear</Boton>
        </form>

        <ul className="divide-y divide-superficie2 text-sm">
          {usuarios.data?.items.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div>
                <span className="font-mono">{u.username}</span>{' '}
                <span className="text-xs text-suave">
                  {u.rol}
                  {u.estado !== 'ACTIVO' ? ' · INACTIVO' : ''}
                </span>
              </div>
              {claveDe === u.id ? (
                <form
                  className="flex flex-wrap items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    resetearClave.mutate({ usuarioId: u.id, password: claveNueva.trim() || undefined });
                  }}
                >
                  <input
                    className="campo min-h-10 w-56 text-sm"
                    placeholder="Clave nueva (vacío = generar)"
                    value={claveNueva}
                    onChange={(e) => setClaveNueva(e.target.value)}
                    minLength={claveNueva ? 8 : undefined}
                    autoFocus
                  />
                  <Boton type="submit" tamano="sm" disabled={resetearClave.isPending}>Guardar</Boton>
                  <Boton
                    variante="texto"
                    tamano="sm"
                    onClick={() => {
                      setClaveDe(null);
                      setClaveNueva('');
                    }}
                  >
                    Cancelar
                  </Boton>
                </form>
              ) : (
                <div className="flex gap-2">
                  <Boton
                    variante="contorno"
                    tamano="sm"
                    onClick={() => {
                      setClaveDe(u.id);
                      setClaveNueva('');
                    }}
                  >
                    Cambiar contraseña
                  </Boton>
                  <Boton variante="texto" tamano="sm" onClick={() => alternarUsuario.mutate(u)}>
                    {u.estado === 'ACTIVO' ? 'Desactivar' : 'Activar'}
                  </Boton>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Tarjeta>
    </main>
  );
}
